/**
 * Hook useCalendars
 *
 * Lista calendários visíveis ao usuário via RPC list_user_calendars
 * com a permissão resolvida (manage/edit/view/free_busy).
 *
 * RELEASE:
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 2 Calendário Global)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useCalendars');

export interface UserCalendar {
  id: string;
  tenant_id: string;
  owner_user_id: string;
  name: string;
  color: string;
  description: string | null;
  visibility: 'private' | 'tenant' | 'shared';
  google_integration_id: string | null;
  google_calendar_id: string | null;
  is_primary: boolean;
  is_active: boolean;
  user_permission: 'manage' | 'edit' | 'view' | 'free_busy' | 'none';
  is_owner: boolean;
  /** v1.2.0: nome do dono do calendário (para distinguir calendários homônimos). */
  owner_full_name?: string | null;
  /** v1.2.0: email do dono como fallback quando full_name é nulo. */
  owner_email?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Lista calendários visíveis ao usuário no tenant atual.
 */
export function useCalendars() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['user-calendars', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      logger.info('Listando calendários do usuário');
      const { data, error } = await supabase.rpc('list_user_calendars', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar calendários', error);
        throw error;
      }
      return (data ?? []) as UserCalendar[];
    },
  });

  /** Cria calendário (manual ou vinculado a uma integração Google). */
  const createMutation = useMutation({
    mutationFn: async (input: {
      name: string;
      color?: string;
      visibility?: 'private' | 'tenant' | 'shared';
      google_integration_id?: string | null;
      google_calendar_id?: string | null;
      is_primary?: boolean;
      description?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('create_calendar', {
        p_name: input.name,
        p_color: input.color ?? '#3B82F6',
        p_visibility: input.visibility ?? 'private',
        p_google_integration_id: input.google_integration_id ?? null,
        p_google_calendar_id: input.google_calendar_id ?? null,
        p_is_primary: input.is_primary ?? false,
        p_description: input.description ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-calendars'] });
      toast.success('Calendário criado');
    },
    onError: (e: any) => {
      logger.error('Erro ao criar calendário', e);
      toast.error(e?.message ?? 'Erro ao criar calendário');
    },
  });

  /** Atualiza calendário (parcial). */
  const updateMutation = useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      color?: string;
      description?: string | null;
      visibility?: 'private' | 'tenant' | 'shared';
      is_active?: boolean;
      is_primary?: boolean;
    }) => {
      const { error } = await supabase.rpc('update_calendar', {
        p_calendar_id: input.id,
        p_name: input.name ?? null,
        p_color: input.color ?? null,
        p_description: input.description ?? null,
        p_visibility: input.visibility ?? null,
        p_is_active: input.is_active ?? null,
        p_is_primary: input.is_primary ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-calendars'] });
    },
    onError: (e: any) => {
      logger.error('Erro ao atualizar calendário', e);
      toast.error('Erro ao atualizar calendário');
    },
  });

  /** Exclui calendário (e seus eventos). */
  const deleteMutation = useMutation({
    mutationFn: async (calendarId: string) => {
      const { error } = await supabase.rpc('delete_calendar', {
        p_calendar_id: calendarId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-calendars'] });
      toast.success('Calendário excluído');
    },
    onError: (e: any) => {
      logger.error('Erro ao excluir calendário', e);
      toast.error('Erro ao excluir calendário');
    },
  });

  return {
    calendars: query.data ?? [],
    isLoading: query.isLoading,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
  };
}
